import React, {DetailedHTMLProps, Dispatch, ImgHTMLAttributes, useState} from "react";
import {IWavePosImage} from "../../types/wave-pos-image";
import axios from "../../services/axios";
import npiApi, {npiApiDownloadFileCallback} from "../../services/api";
import {Button, Modal} from "antd";
import {DownloadOutlined, WarningOutlined} from "@ant-design/icons";
import Box from "@2fd/ant-design-icons/lib/Box";
import styled from "styled-components";
import baseAxios from "axios";
import useAcl from "../../hooks/use-acl";
import {DeleteOutlined} from "@ant-design/icons/lib";
import {useRequest} from "ahooks";

interface NpiGalleryImageProps extends DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
    image: IWavePosImage,
    isThumb?: boolean,
    withLinks?: boolean,
    onRemove?: Dispatch<number>,
}

const NpiGalleryImage = ({image, isThumb, withLinks, onRemove, ...props}: NpiGalleryImageProps) => {
    const {isWWSuperAdmin} = useAcl();

    const imageSrc = !!isThumb
        ? (image.box_thumbnail_route ? image.box_thumbnail_route : image.thumbnail_route)
        : (image.box_image_route ? image.box_image_route : image.image_route);

    const [downloading, setDownloading] = useState<boolean>(false)
    const {runAsync: runDelete, loading: deleting} = useRequest(npiApi.internal.gallery.deleteImage, { manual: true,
        onSuccess: () => onRemove?.(image.id),
    });

    const onDownload = (e: any) => {
        e.stopPropagation();
        setDownloading(true)
        onDownloadImage(image).finally(() => setDownloading(false))
    }

    const onOpenBox = (e: any) => {
        e.stopPropagation();
        window.open(image.box_link, "_blank");
    }

    const onDelete = (e: any) => {
        e.stopPropagation();

        if(image.is_answer) {
            Modal.error({
                title: "Cannot remove answer image",
                content: <>
                    This image is an answer to a survey question.
                    <br/>It cannot be removed from here, but you can remove it by re-submitting the survey directly on the external application.
                </>
            })
        }

        else {
            Modal.confirm({
                content: <>

                    <div style={{marginBottom: "10px", display: "flex", gap: "10px", flexWrap: "nowrap"}}>
                        <img src={imageSrc} alt={image.original_name} style={{maxWidth: "60px", maxHeight: "60px"}}/>
                        <p>
                            Are you sure you want to remove the image from the server ?
                            <br/><strong>This operation cannot be undone !</strong>
                        </p>
                    </div>

                    {(!!image.box_link) && <>
                        <em style={{color: "red"}}>
                            <WarningOutlined/>

                            This image is stored on Box.
                            <br/>Please remove it from the Box platform, before removing it here.
                            <br/> <a href={image.box_link} target={"_blank"} rel="noreferrer">{image.box_link}</a>
                        </em>
                    </>}
                </>,
                onOk: () => {
                    runDelete({id: image.id})
                }
            })
        }
    }

    return <StyledDiv className={'img-container'}>
        <img src={imageSrc} alt={image.original_name} {...props}/>
        {withLinks && <div className={'img-btn-container'}>
            {(isWWSuperAdmin && onRemove) &&
                <Button color={"red"} loading={deleting} icon={<DeleteOutlined/>} className={'img-btn btn-delete'} shape={'circle'} onClick={onDelete} title={"Remove image from server"}/>
            }

            {!!image.box_link &&
                <Button icon={<Box/>} className={'img-btn btn-box'} shape={'circle'} onClick={onOpenBox} title={"Box"}/>
            }
            <Button loading={downloading} icon={<DownloadOutlined/>} className={'img-btn btn-download'} shape={'circle'} onClick={onDownload} title={"Download"}/>
        </div>}
    </StyledDiv>
}

export const onDownloadImage = async (image: IWavePosImage): Promise<any> => {

    // rename file for download with pos, wave and question information
    const ext = image.original_name.split(".").pop();
    let filenameDownload = `w${image.wave_id}_p${image.pos_id}`;
    if( image.question_id ){
        filenameDownload +=  ` - Q${image.question_id}_${image.question_label}`;
    }
    filenameDownload += ` - ${image.id}.${ext}`;

    if( !!image.box_image_route ) {
        return baseAxios.get(image.box_image_route, {responseType: 'blob'}).then(npiApiDownloadFileCallback( filenameDownload ))
    } else {
        return axios.get(image.image_route, {responseType: 'blob', rawResponse: true}).then(npiApiDownloadFileCallback( filenameDownload ))
    }
}

const StyledDiv = styled.div`
    position: relative;
    width: 100%;
    .img-btn-container {
        position: absolute;
        bottom: 15px;
        right: 15px;
        display: flex;
        flex-direction: column;
        gap: 5px;
        
        .img-btn {
            background: #fffc;
            display: inline-block;
            &.btn-box .anticon{
                font-size: 22px
            }
            &.btn-delete {
                color: red;
            }
        }
    }
`

export default NpiGalleryImage;